import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
import { getApiResponseMessage } from "../../utils/apiErrorMessage";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Fetch all quotation request customers
export const fetchQuotationRequestCustomers = createAsyncThunk(
  "quotationRequestCustomers/fetchAll",
  async (params = {}) => {
    try {
      const response = await axios.get(`${BASE_URL}/quotations-request-customers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
        params,
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      throw new Error(message);
    }
  }
);

// Fetch single quotation request customer
export const fetchQuotationRequestCustomer = createAsyncThunk(
  "quotationRequestCustomers/fetchOne",
  async (id) => {
    try {
      const response = await axios.get(`${BASE_URL}/quotations-request-customers/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      throw new Error(message);
    }
  }
);

// Add quotation request customer
export const addQuotationRequestCustomer = createAsyncThunk(
  "quotationRequestCustomers/add",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/quotations-request-customers`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      if (response.data?.status === false) {
        return rejectWithValue(
          typeof response.data === "object" && response.data !== null
            ? response.data
            : { message: "Failed to add data" }
        );
      }
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue(
        typeof data === "object" && data !== null
          ? data
          : { message: "Failed to add data" }
      );
    }
  }
);

// Update quotation request customer
export const updateQuotationRequestCustomer = createAsyncThunk(
  "quotationRequestCustomers/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/quotations-request-customers/update/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      if (response.data?.status === false) {
        return rejectWithValue(
          typeof response.data === "object" && response.data !== null
            ? response.data
            : { message: "Failed to update data" }
        );
      }
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue(
        typeof data === "object" && data !== null
          ? data
          : { message: "Failed to update data" }
      );
    }
  }
);

// Update quotation request customer status
export const updateQuotationRequestCustomerStatus = createAsyncThunk(
  "quotationRequestCustomers/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);

      const response = await axios.post(
        `${BASE_URL}/quotations-request-customers/${id}/status`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return { id, status, message: response.data?.message };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update status";
      return rejectWithValue({ message });
    }
  }
);

// Delete quotation request customer
export const deleteQuotationRequestCustomer = createAsyncThunk(
  "quotationRequestCustomers/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/quotations-request-customers/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete data";
      return rejectWithValue({ message });
    }
  }
);

// Fetch service types (/api/service-types) with pagination
export const fetchServiceTypes = createAsyncThunk(
  "quotationRequestCustomers/fetchServiceTypes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] != null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/service-types${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load service types";
      return rejectWithValue({ message });
    }
  }
);

// Fetch service types from /api/service-types (for filtering)
export const fetchServiceTypesForFilter = createAsyncThunk(
  "quotationRequestCustomers/fetchServiceTypesForFilter",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/service-types${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load service types";
      throw new Error(message);
    }
  }
);

// Fetch transport types
export const fetchTransportTypes = createAsyncThunk(
  "quotationRequestCustomers/fetchTransportTypes",
  async () => {
    try {
      const response = await axios.get(`${BASE_URL}/transport-types`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load transport types";
      throw new Error(message);
    }
  }
);

// Fetch clients (paginated)
export const fetchClients = createAsyncThunk(
  "quotationRequestCustomers/fetchClients",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/clients${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load clients";
      return rejectWithValue({ message });
    }
  }
);

// Fetch cities
export const fetchCities = createAsyncThunk(
  "quotationRequestCustomers/fetchCities",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/cities${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load cities";
      throw new Error(message);
    }
  }
);

// Add attachments
export const addAttachments = createAsyncThunk(
  "quotationRequestCustomers/addAttachments",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/attachments-quotations-request-customers`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add attachments";
      return rejectWithValue({ message });
    }
  }
);

// Fetch quotation requests
export const fetchQuotationRequests = createAsyncThunk(
  "quotationRequestCustomers/fetchQuotationRequests",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/quotations-requests${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load quotation requests";
      throw new Error(message);
    }
  }
);

// Fetch quotation request suppliers
export const fetchQuotationRequestSuppliers = createAsyncThunk(
  "quotationRequestCustomers/fetchQuotationRequestSuppliers",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/quotations-request-suppliers${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load quotation request suppliers";
      throw new Error(message);
    }
  }
);

// Fetch winch partners
export const fetchWinchPartners = createAsyncThunk(
  "quotationRequestCustomers/fetchWinchPartners",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/partners${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      throw new Error(message);
    }
  }
);

// Fetch winch branches
export const fetchWinchBranches = createAsyncThunk(
  "quotationRequestCustomers/fetchWinchBranches",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/winch-branches${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch branches";
      throw new Error(message);
    }
  }
);

// Fetch business sectors
export const fetchBusinessSectors = createAsyncThunk(
  "quotationRequestCustomers/fetchBusinessSectors",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/business-sectors${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sectors";
      return rejectWithValue({ message });
    }
  }
);

const QuotationRequestCustomersSlice = createSlice({
  name: "quotationRequestCustomers",
  initialState: {
    data: [],
    pagination: null,
    record: null,
    serviceTypes: [],
    serviceTypesPagination: null,
    serviceTypesLoading: false,
    serviceTypesForFilter: [],
    transportTypes: [],
    clients: [],
    clientsPagination: null,
    clientsLoading: false,
    businessSectors: [],
    businessSectorsPagination: null,
    businessSectorsLoading: false,
    winchPartners: [],
    winchBranches: [],
    cities: [],
    quotationRequests: [],
    quotationRequestSuppliers: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchQuotationRequestCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationRequestCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchQuotationRequestCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      // Fetch one
      .addCase(fetchQuotationRequestCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationRequestCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchQuotationRequestCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      // Add
      .addCase(addQuotationRequestCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addQuotationRequestCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = getApiResponseMessage(action.payload, "");
      })
      .addCase(addQuotationRequestCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = getApiResponseMessage(action.payload, "Failed to add data");
      })
      // Update
      .addCase(updateQuotationRequestCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateQuotationRequestCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = getApiResponseMessage(action.payload, "");
      })
      .addCase(updateQuotationRequestCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = getApiResponseMessage(action.payload, "Failed to update data");
      })
      // Update status
      .addCase(updateQuotationRequestCustomerStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateQuotationRequestCustomerStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        const { id, status } = action.payload;
        const row = state.data.find((item) => String(item.id) === String(id));
        if (row) row.status = status;
      })
      .addCase(updateQuotationRequestCustomerStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to update status";
      })
      // Delete
      .addCase(deleteQuotationRequestCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteQuotationRequestCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteQuotationRequestCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to delete data";
      })
      // Fetch service types
      .addCase(fetchServiceTypes.pending, (state) => {
        state.serviceTypesLoading = true;
      })
      .addCase(fetchServiceTypes.fulfilled, (state, action) => {
        state.serviceTypesLoading = false;
        const page = action.meta.arg?.page || 1;
        const rows = action.payload?.data?.data || [];
        if (page === 1) {
          state.serviceTypes = rows;
        } else {
          const existingIds = new Set((state.serviceTypes || []).map((s) => s.id));
          const merged = [...(state.serviceTypes || [])];
          rows.forEach((row) => {
            if (!existingIds.has(row.id)) merged.push(row);
          });
          state.serviceTypes = merged;
        }
        state.serviceTypesPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchServiceTypes.rejected, (state, action) => {
        state.serviceTypesLoading = false;
        state.error = action.payload?.message || "Failed to load service types";
      })
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.clientsLoading = true;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.clientsLoading = false;
        const page = action.meta.arg?.page || 1;
        const rows = action.payload?.data?.data || [];
        if (page === 1) {
          state.clients = rows;
        } else {
          const existingIds = new Set((state.clients || []).map((c) => c.id));
          const merged = [...(state.clients || [])];
          rows.forEach((row) => {
            if (!existingIds.has(row.id)) merged.push(row);
          });
          state.clients = merged;
        }
        state.clientsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.clientsLoading = false;
        state.error = action.payload?.message || "Failed to load clients";
      })
      // Fetch cities
      .addCase(fetchCities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = action.payload || [];
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load cities";
      })
      // Add attachments
      .addCase(addAttachments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addAttachments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addAttachments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to add attachments";
      })
      // Fetch quotation requests
      .addCase(fetchQuotationRequests.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchQuotationRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationRequests = action.payload || [];
      })
      .addCase(fetchQuotationRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load quotation requests";
      })
      // Fetch quotation request suppliers
      .addCase(fetchQuotationRequestSuppliers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchQuotationRequestSuppliers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationRequestSuppliers = action.payload || [];
      })
      .addCase(fetchQuotationRequestSuppliers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load quotation request suppliers";
      })
      // Fetch winch partners
      .addCase(fetchWinchPartners.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWinchPartners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winchPartners = action.payload || [];
      })
      .addCase(fetchWinchPartners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load winch partners";
      })
      // Fetch winch branches
      .addCase(fetchWinchBranches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWinchBranches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winchBranches = action.payload || [];
      })
      .addCase(fetchWinchBranches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load winch branches";
      })
      // Fetch business sectors
      .addCase(fetchBusinessSectors.pending, (state) => {
        state.businessSectorsLoading = true;
      })
      .addCase(fetchBusinessSectors.fulfilled, (state, action) => {
        state.businessSectorsLoading = false;
        const page = action.meta.arg?.page || 1;
        const rows = action.payload?.data?.data || [];
        if (page === 1) {
          state.businessSectors = rows;
        } else {
          const existingIds = new Set((state.businessSectors || []).map((s) => s.id));
          const merged = [...(state.businessSectors || [])];
          rows.forEach((row) => {
            if (!existingIds.has(row.id)) merged.push(row);
          });
          state.businessSectors = merged;
        }
        state.businessSectorsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchBusinessSectors.rejected, (state, action) => {
        state.businessSectorsLoading = false;
        state.error = action.payload?.message || "Failed to load business sectors";
      })
      // Fetch service types (from /api/service-types)
      .addCase(fetchServiceTypesForFilter.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchServiceTypesForFilter.fulfilled, (state, action) => {
        state.isLoading = false;
        state.serviceTypesForFilter = action.payload || [];
      })
      .addCase(fetchServiceTypesForFilter.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load service types";
      })
      // Fetch transport types
      .addCase(fetchTransportTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTransportTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transportTypes = action.payload || [];
      })
      .addCase(fetchTransportTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load transport types";
      });
  },
});

export const { clearState } = QuotationRequestCustomersSlice.actions;
export default QuotationRequestCustomersSlice.reducer;

